/**
 * CR402 Phase 2 + CR407: Components Studio - Master/Detail Editor with 50/50 Split
 * CR403: Hardened against undefined crashes + ErrorBoundary fallback
 * CR406-A: Preview Safe Mode - localStorage-based toggle to disable previews
 * CR407: Resizable 50/50 split, component dropdown selector, persistent layout
 * 
 * Redesigned Components editor with:
 * - Sticky top bar (deal type picker, component selector, save/discard, debug menu)
 * - Left pane: Component selector dropdown + editor controls below
 * - Right pane: Sticky preview (Draft + Live full card with highlight)
 * - Default 50/50 resizable split with drag handle
 * - Persistent split ratio in localStorage
 * - Highlight overlay for selected component
 * - Draft vs Live state management with stable hashing
 * - Preview Safe Mode: bypass preview rendering to avoid crashes during debugging
 */

import { useState, useMemo, useEffect, useCallback } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  Save, 
  Undo2, 
  AlertCircle,
  Info,
  Bug,
  EyeOff,
  Eye,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";

// Types
import type { 
  ComponentsConfig, 
} from "./DealCardsTab";
import type { CardStyling } from "./CardStylingPanel";
import type { ExtendedSectionToggles } from "./SectionTogglesPanel";
import type { DealTypeRegistryEntry, DealCardTemplate, CardTemplateLabels } from "@/providers/DealCardConfigProvider";
import type { RibbonDraft } from "./RibbonSettingsPanel";
import type { HeaderComposition } from "./HeaderCompositionPanel";
import type { RibbonStylingConfig } from "@/components/admin/deal-cards/RibbonStylingSection";
import { useGlobalTheme } from "@/providers/GlobalThemeProvider";

// Subcomponents
import { type ComponentCatalogItem } from "./ComponentsList";
import { ComponentEditorPanel } from "./ComponentEditorPanel";
import { LiveCardPreviewWithHighlight } from "./LiveCardPreviewWithHighlight";
import { DealCardsDebugPanel } from "./DealCardsDebugPanel";
import { DraftComponentPreview } from "./DraftComponentPreview";

// Hooks
import { useDealSourceMode } from "@/hooks/useDealSourceMode";
import { DealSourcePicker } from "@/components/admin/deal-cards/DealSourcePicker";
import { DEFAULT_FACES_CONFIG, type CardFacesConfig } from "@/lib/deals/cardFaces";
// SampleDealPicker removed — CR425/CR426: using DealSourcePicker instead
import { computeConfigHash } from "@/hooks/useStablePreviewKey";
import { safeLower, safeStr, safeTrim } from "@/lib/safeString";
import { DEFAULT_SPEC_V2 } from "@/lib/deal-cards/specV2/resolveSpec";
import { stableEquals } from "@/lib/utils/stableCompare";
import type { SaveTrace } from "@/lib/diagnostics/saveTrace";

// ================================================================
// COMPONENT CATALOG
// ================================================================

export const COMPONENT_CATALOG: ComponentCatalogItem[] = [
  { key: "header", label: "Header (Front)", group: "Core", description: "Top gradient band with logo and title" },
  { key: "header_back", label: "Header (Back)", group: "Core", description: "Back face header strip with mode selector" },
  { key: "ribbon", label: "Ribbon", group: "Banners", description: "Corner badge with deal type label" },
  { key: "starburst", label: "Starburst", group: "Banners", description: "Value highlight badge" },
  { key: "watermark", label: "Watermark", group: "Banners", description: "Subtle background graphic" },
  { key: "promo_badges", label: "Promo Badges", group: "Core", description: "Membership, corporate, bulk badges" },
  { key: "headline", label: "Headline", group: "Core", description: "Deal mechanic text" },
  { key: "details_pills", label: "Details Pills", group: "Core", description: "Discount tiers and details" },
  { key: "value_box", label: "Value Box", group: "Value", description: "Effective value display" },
  { key: "spend_box", label: "Spend Box", group: "Value", description: "Qualifying spend display" },
  { key: "reward_box", label: "Reward Box", group: "Value", description: "Reward amount display" },
  { key: "dates", label: "Dates", group: "Core", description: "Validity dates" },
  { key: "cta_button", label: "CTA Button", group: "Actions", description: "Primary action button" },
  { key: "actions", label: "Actions Row", group: "Actions", description: "View deal, T&Cs buttons" },
  { key: "feedback", label: "Feedback", group: "Actions", description: "Thumbs feedback state and reset control" },
  { key: "card_snippet", label: "Card Snippet", group: "Core", description: "Cropped flyer snippet image with eligibility gating" },
  { key: "labels", label: "Labels", group: "Actions", description: "Override card text labels" },
];

// ================================================================
// TYPES
// ================================================================

export interface ComponentsStudioProps {
  // Context
  registry: DealTypeRegistryEntry[];
  templates: DealCardTemplate[];
  selectedType: string;
  onSelectedTypeChange: (type: string) => void;
  
  // Draft state
  draftComponents: ComponentsConfig;
  draftStyling: CardStyling;
  draftSections: ExtendedSectionToggles;
  draftSurfaceBlockToggles: { primary: Record<string, boolean>; details: Record<string, boolean> };
  draftRibbon: RibbonDraft;
  draftHeader: HeaderComposition;
  draftLabels: CardTemplateLabels;
  draftSectionOrder: string[];
  draftSurfaceBlockOrder: { primary: string[]; details: string[] };
  themePreset: "light" | "dark" | "custom";
  
  // Saved state for dirty detection
  savedComponents?: ComponentsConfig;
  savedStyling?: CardStyling;
  
  // Handlers
  onComponentsChange: (update: Partial<ComponentsConfig>) => void;
  onStylingChange: (update: Partial<CardStyling>) => void;
  onLabelsChange?: (update: Record<string, string>) => void;
  onSave: () => Promise<void>;
  onDiscard: () => void;
  
  // State flags
  hasUnsavedChanges: boolean;
  saving?: boolean;
  
  // CR340: Save trace for debug panel
  lastSaveTrace?: SaveTrace | null;
  
  // CR340: Saved ribbon for apply-gating fallback
  savedRibbon?: RibbonDraft;
}

// ================================================================
// STABLE HASH HELPER
// ================================================================

/**
 * CR403: Hardened stable hash that never throws on undefined/null
 */
function stableHash(config: unknown): string {
  // CR403: Ensure we never pass undefined to computeConfigHash
  return computeConfigHash(config ?? null);
}

// ================================================================
// LOCAL STORAGE FOR LAST PREVIEW
// ================================================================

const LAST_PREVIEW_KEY_PREFIX = "componentsStudio:lastPreview:";

function getLastPreview(dealTypeKey: string): { dealId: string; updatedAt: string } | null {
  try {
    const stored = localStorage.getItem(`${LAST_PREVIEW_KEY_PREFIX}${dealTypeKey}`);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function setLastPreview(dealTypeKey: string, dealId: string) {
  try {
    localStorage.setItem(
      `${LAST_PREVIEW_KEY_PREFIX}${dealTypeKey}`,
      JSON.stringify({ dealId, updatedAt: new Date().toISOString() })
    );
  } catch {
    // Ignore storage errors
  }
}

// ================================================================
// CR406-A: PREVIEW SAFE MODE HELPERS
// ================================================================

const PREVIEW_ENABLED_KEY = "ff_componentsStudio_previews";
const DEBUG_LOGGING_KEY = "ff_componentsStudio_debugLogging";
const SPLIT_RATIO_KEY = "dealCards.components.splitRatio";
const SELECTED_COMPONENT_PREFIX = "dealCards.components.selectedComponent";

function getPreviewsEnabled(): boolean {
  try {
    const stored = localStorage.getItem(PREVIEW_ENABLED_KEY);
    // Default: false (previews OFF for safety)
    return stored === "true";
  } catch {
    return false;
  }
}

function setPreviewsEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(PREVIEW_ENABLED_KEY, enabled ? "true" : "false");
  } catch {
    // Ignore storage errors
  }
}

function getDebugLoggingEnabled(): boolean {
  try {
    const stored = localStorage.getItem(DEBUG_LOGGING_KEY);
    return stored === "true";
  } catch {
    return false;
  }
}

function setDebugLoggingEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(DEBUG_LOGGING_KEY, enabled ? "true" : "false");
  } catch {
    // Ignore storage errors
  }
}

// CR407: Split ratio persistence
function getSavedSplitRatio(): number {
  try {
    const stored = localStorage.getItem(SPLIT_RATIO_KEY);
    if (stored) {
      const ratio = parseFloat(stored);
      if (!isNaN(ratio) && ratio >= 20 && ratio <= 80) {
        return ratio;
      }
    }
    return 50; // Default 50/50 split
  } catch {
    return 50;
  }
}

function saveSplitRatio(ratio: number): void {
  try {
    localStorage.setItem(SPLIT_RATIO_KEY, String(ratio));
  } catch {
    // Ignore storage errors
  }
}

// CR407: Selected component persistence per deal type
function getSelectedComponent(dealTypeKey: string): string | null {
  try {
    const stored = localStorage.getItem(`${SELECTED_COMPONENT_PREFIX}.${dealTypeKey}`);
    // Validate it's a valid component key
    if (stored && COMPONENT_CATALOG.some(c => c.key === stored)) {
      return stored;
    }
    return null;
  } catch {
    return null;
  }
}

function saveSelectedComponent(dealTypeKey: string, componentKey: string): void {
  try {
    localStorage.setItem(`${SELECTED_COMPONENT_PREFIX}.${dealTypeKey}`, componentKey);
  } catch {
    // Ignore storage errors
  }
}

// ================================================================
// GROUP LABELS FOR DROPDOWN
// ================================================================

const GROUP_ORDER: Array<"Core" | "Banners" | "Value" | "Actions"> = ["Core", "Banners", "Value", "Actions"];

// ================================================================
// COMPONENT
// ================================================================

export function ComponentsStudio({
  registry,
  templates,
  selectedType,
  onSelectedTypeChange,
  draftComponents,
  draftStyling,
  draftSections,
  draftSurfaceBlockToggles,
  draftRibbon,
  draftHeader,
  draftLabels,
  draftSectionOrder,
  draftSurfaceBlockOrder,
  themePreset,
  savedComponents,
  savedStyling,
  onComponentsChange,
  onStylingChange,
  onLabelsChange,
  onSave,
  onDiscard,
  hasUnsavedChanges,
  saving = false,
  lastSaveTrace,
  savedRibbon,
}: ComponentsStudioProps) {
  // CR326: Use selectedType directly — never fall back to "BONUS" (no cross-type bleed)
  const safeSelectedType = safeTrim(safeStr(selectedType, "")) || selectedType || "UNCLASSIFIED";
  
  // CR305: Get Theme Labs tokens for ribbon color SSOT
  const { tokens: globalTokens } = useGlobalTheme();
  const dealTypeThemes = globalTokens.dealTypeThemes ?? null;
  
  // CR406-A: Preview Safe Mode state
  const [previewsEnabled, setPreviewsEnabledState] = useState(getPreviewsEnabled);
  const [debugLogging, setDebugLoggingState] = useState(getDebugLoggingEnabled);
  
  // CR407: Panel split ratio state
  const [leftPanelSize, setLeftPanelSize] = useState(getSavedSplitRatio);
  
  // CR407: Handle panel resize
  const handlePanelResize = useCallback((sizes: number[]) => {
    if (sizes[0]) {
      setLeftPanelSize(sizes[0]);
      saveSplitRatio(sizes[0]);
    }
  }, []);
  
  // CR406-A: Toggle handlers with persistence
  const togglePreviews = useCallback(() => {
    const newValue = !previewsEnabled;
    setPreviewsEnabledState(newValue);
    setPreviewsEnabled(newValue);
    if (debugLogging) {
      console.log("[CR406-A] Previews toggled:", newValue ? "ENABLED" : "DISABLED");
    }
  }, [previewsEnabled, debugLogging]);
  
  const toggleDebugLogging = useCallback(() => {
    const newValue = !debugLogging;
    setDebugLoggingState(newValue);
    setDebugLoggingEnabled(newValue);
    console.log("[CR406-A] Debug logging toggled:", newValue ? "ENABLED" : "DISABLED");
  }, [debugLogging]);
  
  // CR406-A: Log debug payload when enabled
  useEffect(() => {
    if (debugLogging) {
      console.log("[CR406-A] Debug payload:", {
        selectedType: safeSelectedType,
        draftComponents,
        draftStyling,
        draftSections,
        draftRibbon,
        draftHeader,
        previewsEnabled,
      });
    }
  }, [debugLogging, safeSelectedType, draftComponents, draftStyling, draftSections, draftRibbon, draftHeader, previewsEnabled]);
  
  // CR407: Selected component for editing - persist per deal type
  const [selectedComponentKey, setSelectedComponentKey] = useState<string | null>(() => {
    // Try to restore last selected component for this deal type
    const saved = getSelectedComponent(safeSelectedType);
    if (saved) return saved;
    // Default to first enabled component or "header"
    return "header";
  });
  
  // CR407: Update selected component when deal type changes
  useEffect(() => {
    const saved = getSelectedComponent(safeSelectedType);
    if (saved) {
      setSelectedComponentKey(saved);
    }
  }, [safeSelectedType]);
  
  // CR407: Persist selected component on change
  const handleSelectComponent = useCallback((key: string | null) => {
    setSelectedComponentKey(key);
    if (key) {
      saveSelectedComponent(safeSelectedType, key);
    }
  }, [safeSelectedType]);
  
  // CR426: Deal source mode (Sample ↔ Live)
  const [facesConfig, setFacesConfig] = useState<CardFacesConfig>(DEFAULT_FACES_CONFIG);
  
  const dealSource = useDealSourceMode("staging", safeSelectedType);
  const { deal: ssotDeal, meta: ssotMeta, loading: ssotLoading, error: ssotError } = dealSource;
  
  // CR340: Apply-gated preview state
  const [appliedDraft, setAppliedDraft] = useState<{
    components: ComponentsConfig;
    styling: CardStyling;
    ribbon: RibbonDraft;
  } | null>(null);
  
  // CR340: Clear applied draft when saved values change (after save/fetch)
  useEffect(() => {
    setAppliedDraft(null);
  }, [savedComponents, savedStyling]);
  
  // CR340: Apply handler — copies editing state to preview state
  const handleApply = useCallback(() => {
    setAppliedDraft({
      components: draftComponents,
      styling: draftStyling,
      ribbon: draftRibbon,
    });
    toast.info("Preview updated with current edits");
  }, [draftComponents, draftStyling, draftRibbon]);
  
  // CR340: Preview state tracking
  const isPreviewStale = useMemo(() => {
    if (!appliedDraft) return false;
    return (
      !stableEquals(draftComponents, appliedDraft.components) ||
      !stableEquals(draftStyling, appliedDraft.styling) ||
      !stableEquals(draftRibbon, appliedDraft.ribbon)
    );
  }, [draftComponents, draftStyling, draftRibbon, appliedDraft]);
  
  const previewStateLabel = useMemo<"disabled" | "saved" | "applied">(() => {
    if (!previewsEnabled) return "disabled";
    if (appliedDraft) return "applied";
    return "saved";
  }, [previewsEnabled, appliedDraft]);
  
  // CR425: Store last successful preview using SSOT deal
  useEffect(() => {
    if (ssotDeal && ssotMeta?.deal_id) {
      setLastPreview(safeSelectedType, ssotMeta.deal_id);
    }
  }, [ssotDeal, ssotMeta, safeSelectedType]);
  
  // CR409: Build enabled map using RESOLVED enabled state (variant defaults + overrides)
  // This fixes the issue where the Studio was not respecting variant defaults correctly.
  // SSoT: blockToggles is the source of truth. Styling showRibbon/showStarburst is NOT used.
  const componentEnabledMap = useMemo(() => {
    const overrideMap = draftSurfaceBlockToggles?.primary ?? {};
    
    // CR409: Get variant defaults from the selected template's template_json
    // Fall back to DEFAULT_SPEC_V2 if template is not available
    const selectedTemplate = templates.find(t => safeLower(t.deal_type_key) === safeLower(safeSelectedType));
    const templateJson = selectedTemplate?.template_json as { surfaces?: { primary?: { blocks?: Array<{ id: string; enabled?: boolean }> } } } | null;
    const variantBlocks = templateJson?.surfaces?.primary?.blocks ?? DEFAULT_SPEC_V2.surfaces.primary.blocks;
    
    // Build defaults from variant: block.enabled !== false means enabled (missing = true)
    const variantDefaults: Record<string, boolean> = {};
    for (const block of variantBlocks) {
      variantDefaults[block.id] = block.enabled !== false;
    }
    
    // CR409: Resolve enabled state: override if present, else variant default, else true
    // This matches the DB resolved_enabled logic exactly
    const resolveEnabled = (blockId: string): boolean => {
      // If there's an explicit override in blockToggles, use it
      if (overrideMap[blockId] !== undefined) {
        return overrideMap[blockId];
      }
      // Otherwise fall back to variant default
      return variantDefaults[blockId] ?? true;
    };
    
    return {
      header: true, // Always enabled
      header_back: true, // Always enabled (back header)
      ribbon: resolveEnabled("ribbon"),
      starburst: resolveEnabled("starburst"),
      promo_badges: resolveEnabled("promo_badges"),
      headline: resolveEnabled("headline"),
      details_pills: resolveEnabled("details_pills"),
      value_box: resolveEnabled("value_box"),
      spend_box: resolveEnabled("spend_box"),
      reward_box: resolveEnabled("reward_box"),
      dates: resolveEnabled("dates"),
      cta_button: resolveEnabled("cta_button"),
      actions: resolveEnabled("actions"),
      feedback: resolveEnabled("feedback"),
      card_snippet: resolveEnabled("card_snippet"),
    };
  }, [draftSurfaceBlockToggles, templates, safeSelectedType]);
  
  // CR403: Compute dirty states per component with hardened null checks
  const componentDirtyMap = useMemo(() => {
    // CR403: Safe extraction with null coalescing
    const draftHeaderFront = (draftComponents as Record<string, unknown>)?.header_front ?? null;
    const savedHeaderFront = (savedComponents as Record<string, unknown>)?.header_front ?? null;
    const draftHeaderBack = (draftComponents as Record<string, unknown>)?.header_back ?? null;
    const savedHeaderBack = (savedComponents as Record<string, unknown>)?.header_back ?? null;
    const draftRibbonComp = (draftComponents as Record<string, unknown>)?.ribbon ?? null;
    const savedRibbonComp = (savedComponents as Record<string, unknown>)?.ribbon ?? null;
    const draftStarburstComp = (draftComponents as Record<string, unknown>)?.starburst ?? null;
    const savedStarburstComp = (savedComponents as Record<string, unknown>)?.starburst ?? null;
    
    const stylingDirty = stableHash(savedStyling ?? null) !== stableHash(draftStyling ?? null);
    
    // Per-component dirty detection (safe from undefined)
    return {
      header: stableHash(draftHeaderFront) !== stableHash(savedHeaderFront),
      header_back: stableHash(draftHeaderBack) !== stableHash(savedHeaderBack),
      ribbon: stableHash(draftRibbonComp) !== stableHash(savedRibbonComp) || stylingDirty,
      starburst: stableHash(draftStarburstComp) !== stableHash(savedStarburstComp),
      // For other components, check if overall config changed
      promo_badges: false,
      headline: false,
      details_pills: false,
      value_box: false,
      spend_box: false,
      reward_box: false,
      dates: false,
      cta_button: false,
      actions: false,
      feedback: false,
      card_snippet: false,
    };
  }, [draftComponents, savedComponents, draftStyling, savedStyling]);
  
  // CR340: Effective values for preview (applied > saved > draft)
  const effectiveComponents = appliedDraft?.components ?? savedComponents ?? draftComponents;
  const effectiveStyling = appliedDraft?.styling ?? savedStyling ?? draftStyling;
  const effectiveRibbon = appliedDraft?.ribbon ?? savedRibbon ?? draftRibbon;
  
  // CR343: Build V2 draft overrides for the Full Card Preview (replaces registryOverride + templateOverride)
  const safePrimaryToggles = draftSurfaceBlockToggles?.primary ?? {};
  const safeDetailsToggles = draftSurfaceBlockToggles?.details ?? {};
  
  const v2DraftOverrides = useMemo(() => {
    return {
      styleOverrides: {
        ...(effectiveStyling ?? {}),
        showRibbon: effectiveStyling?.showRibbon ?? true,
        showStarburst: effectiveStyling?.showStarburst ?? true,
        showMerchantLogo: effectiveStyling?.showMerchantLogo ?? true,
        showMerchantName: effectiveStyling?.showMerchantName ?? true,
      },
      labels: (draftLabels ?? {}) as Record<string, string>,
      header: (draftHeader ?? {}) as any,
      surfaceOverrides: {
        primary: { blockToggles: safePrimaryToggles },
        details: { blockToggles: safeDetailsToggles },
      },
    };
  }, [effectiveStyling, draftLabels, draftHeader, safePrimaryToggles, safeDetailsToggles]);

  // Ribbon styling config from draftStyling
  const ribbonStyling: RibbonStylingConfig = {
    ribbonBaseHex: draftStyling.ribbonBaseHex,
    ribbonBorderHex: draftStyling.ribbonBorderHex,
    gradientFrom: draftStyling.ribbonGradientFrom,
    gradientTo: draftStyling.ribbonGradientTo,
    borderColor: draftStyling.ribbonBorderColor,
  };
  
  // Handle save — parent (saveComponents) handles toasts + trace, so no wrapping needed
  const handleSave = async () => {
    await onSave();
  };
  
  // Selected component info
  const selectedComponent = COMPONENT_CATALOG.find(c => c.key === selectedComponentKey);
  
  // CR407: Group components for dropdown, CR416: deal-type-aware labels
  const groupedComponents = useMemo(() => {
    const groups: Record<string, ComponentCatalogItem[]> = {};
    for (const comp of COMPONENT_CATALOG) {
      if (!groups[comp.group]) {
        groups[comp.group] = [];
      }
      // CR416: Relabel reward_box as "Discount Box" for DISCOUNT deals
      const effectiveComp = (comp.key === "reward_box" && safeSelectedType === "DISCOUNT")
        ? { ...comp, label: "Discount Box" }
        : comp;
      groups[effectiveComp.group].push(effectiveComp);
    }
    return groups;
  }, [safeSelectedType]);

  return (
    <div className="flex flex-col h-full">
      {/* CR407: Mount verification banner - visible proof Studio v2 is loaded */}
      {import.meta.env.DEV && (
        <div className="bg-emerald-50 border-b border-emerald-200 px-3 py-1.5 text-[11px] text-emerald-700 font-mono flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          ComponentsStudio v3 (CR407) — {safeSelectedType} — Split: {Math.round(leftPanelSize)}%
        </div>
      )}
      
      {/* ============================================================ */}
      {/* STICKY TOP BAR */}
      {/* ============================================================ */}
      <div className="sticky top-0 z-20 bg-background border-b px-4 py-3 flex-shrink-0">
        <div className="flex flex-wrap items-center gap-3 w-full min-w-0">
        {/* Left: Deal Type Picker + Component Selector */}
        <div className="flex flex-wrap items-center gap-3 min-w-0">
          {/* CR339: Deal Type Dropdown Selector */}
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium text-muted-foreground">Type:</Label>
            <Select value={selectedType} onValueChange={onSelectedTypeChange}>
              <SelectTrigger className="w-[160px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {["BONUS", "DISCOUNT", "POINTS", "IN_KIND", "UNCLASSIFIED"].map((type) => (
                  <SelectItem key={type} value={type} className="text-sm">
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <Separator orientation="vertical" className="h-6" />
          
          {/* CR407: Component Selector Dropdown */}
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium text-muted-foreground">Component:</Label>
            <Select
              value={selectedComponentKey || undefined}
              onValueChange={(value) => handleSelectComponent(value)}
            >
              <SelectTrigger className="w-[200px] h-8">
                <SelectValue placeholder="Select component..." />
              </SelectTrigger>
              <SelectContent>
                {GROUP_ORDER.map((group) => {
                  const groupComps = groupedComponents[group];
                  if (!groupComps || groupComps.length === 0) return null;
                  return (
                    <SelectGroup key={group}>
                      <SelectLabel className="text-xs text-muted-foreground uppercase tracking-wide">
                        {group}
                      </SelectLabel>
                      {groupComps.map((comp) => {
                        const isEnabled = componentEnabledMap[comp.key as keyof typeof componentEnabledMap];
                        const isDirty = componentDirtyMap[comp.key as keyof typeof componentDirtyMap];
                        return (
                          <SelectItem key={comp.key} value={comp.key} className="text-sm">
                            <div className="flex items-center gap-2">
                              <span className={cn(!isEnabled && "text-muted-foreground")}>
                                {comp.label}
                              </span>
                              {isDirty && (
                                <span className="text-[10px] text-amber-600 font-medium">•</span>
                              )}
                              {!isEnabled && (
                                <span className="text-[10px] text-muted-foreground">(off)</span>
                              )}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectGroup>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Right: Debug Menu + Save/Discard */}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {/* CR406: Prominent Preview On/Off toggle */}
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-muted/50 border">
            <span className="text-xs font-medium text-muted-foreground">Preview</span>
            <Switch
              checked={previewsEnabled}
              onCheckedChange={togglePreviews}
              className="data-[state=checked]:bg-primary"
            />
            {previewsEnabled ? (
              <Eye className="h-3.5 w-3.5 text-primary" />
            ) : (
              <EyeOff className="h-3.5 w-3.5 text-muted-foreground" />
            )}
          </div>
          
          {/* CR340: Apply button for apply-gated preview */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleApply}
            disabled={!hasUnsavedChanges && !isPreviewStale}
          >
            Apply to Preview
          </Button>
          
          {isPreviewStale && previewsEnabled && (
            <Badge variant="secondary" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
              Preview stale
            </Badge>
          )}
          
          {hasUnsavedChanges && (
            <Badge variant="secondary" className="text-warning bg-warning/10 border-warning/30">
              <AlertCircle className="h-3 w-3 mr-1" />
              Unsaved changes
            </Badge>
          )}
          
          {/* Debug dropdown (moved preview toggle here is still available) */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-1.5">
                <Bug className="h-4 w-4" />
                Debug
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel>Debug Options</DropdownMenuLabel>
              <DropdownMenuSeparator />
              
              <DropdownMenuItem
                className="flex items-center justify-between cursor-pointer"
                onSelect={(e) => {
                  e.preventDefault();
                  toggleDebugLogging();
                }}
              >
                <div className="flex items-center gap-2">
                  <Bug className="h-4 w-4" />
                  <span>Log debug payload</span>
                </div>
                <Switch
                  checked={debugLogging}
                  onCheckedChange={toggleDebugLogging}
                  className="ml-2"
                />
              </DropdownMenuItem>
              
              <DropdownMenuSeparator />
              <div className="px-2 py-1.5 text-xs text-muted-foreground">
                Debug logging outputs config payloads to the browser console.
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
          
          <Button
            variant="outline"
            size="sm"
            onClick={onDiscard}
            disabled={!hasUnsavedChanges || saving}
          >
            <Undo2 className="h-4 w-4 mr-1" />
            Discard
          </Button>
          
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!hasUnsavedChanges || saving}
          >
            <Save className="h-4 w-4 mr-1" />
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
        </div>
      </div>
      
      {/* ============================================================ */}
      {/* MAIN 2-PANE RESIZABLE LAYOUT (CR407) */}
      {/* ============================================================ */}
      <ResizablePanelGroup
        direction="horizontal"
        className="flex-1"
        onLayout={handlePanelResize}
      >
        {/* ------------------------------------------------------------ */}
        {/* LEFT PANE: Component List + Editor */}
        {/* ------------------------------------------------------------ */}
        <ResizablePanel
          defaultSize={leftPanelSize}
          minSize={30}
          className="flex flex-col overflow-hidden"
        >
          {/* CR340: Component list removed — navigation via dropdowns only */}
          {/* Selected Component Editor */}
          <ScrollArea className="flex-1">
            <div className="p-4">
              {selectedComponent ? (
                <ComponentEditorPanel
                  componentKey={selectedComponentKey!}
                  componentLabel={selectedComponent.label}
                  componentDescription={selectedComponent.description}
                  components={draftComponents}
                  styling={draftStyling}
                  onChange={onComponentsChange}
                  onStylingChange={onStylingChange}
                  structureEnabled={componentEnabledMap[selectedComponentKey as keyof typeof componentEnabledMap] ?? true}
                  sampleDeal={ssotDeal}
                  dealTypeKey={safeSelectedType}
                  labels={(draftLabels ?? {}) as Record<string, string>}
                  onLabelsChange={onLabelsChange}
                  dealTypeThemes={dealTypeThemes}
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-32 text-muted-foreground text-sm">
                  <Info className="h-5 w-5 mb-2" />
                  <p>Select a component to edit</p>
                  <p className="text-xs mt-1">Use the dropdown above or click a component in the list</p>
                </div>
              )}
            </div>
          </ScrollArea>
        </ResizablePanel>
        
        {/* CR407: Resizable Handle with grip */}
        <ResizableHandle withHandle className="bg-border hover:bg-primary/20 transition-colors" />
        
        {/* ------------------------------------------------------------ */}
        {/* RIGHT PANE: Previews (CR407: sticky, takes remaining space) */}
        {/* ------------------------------------------------------------ */}
        <ResizablePanel
          defaultSize={100 - leftPanelSize}
          minSize={30}
          className="flex flex-col overflow-hidden bg-muted/10"
        >
          {/* CR426: Deal Source Picker */}
          <div className="p-3 border-b flex-shrink-0">
            <DealSourcePicker
              mode={dealSource.mode}
              onModeChange={dealSource.setMode}
              meta={ssotMeta}
              loading={ssotLoading}
              liveDeals={dealSource.liveDeals}
              liveDealsLoading={dealSource.liveDealsLoading}
              liveSearch={dealSource.liveSearch}
              onLiveSearchChange={dealSource.setLiveSearch}
              selectedLiveDealId={dealSource.selectedLiveDealId}
              onSelectLiveDeal={dealSource.setSelectedLiveDealId}
            />
            {ssotError && (
              <Badge variant="destructive" className="text-[10px] ml-2">
                {ssotError}
              </Badge>
            )}
          </div>
          
          {/* CR341: Component Preview — live from editing draft */}
          <div className="p-3 border-b flex-shrink-0">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">
                  {selectedComponent?.label || "Component"}
                </Badge>
                <span className="text-xs text-muted-foreground">Component Preview (live)</span>
              </div>
              <div className="border rounded-lg bg-background p-3 min-h-[80px]">
                <DraftComponentPreview
                  componentKey={selectedComponentKey || "header"}
                  deal={ssotDeal as any}
                  components={draftComponents}
                  styling={draftStyling}
                  dealTypeKey={safeSelectedType}
                />
              </div>
            </div>
          </div>
          
          {/* CR340: Debug Preview Panel */}
          <div className="p-3 border-b flex-shrink-0">
            <DealCardsDebugPanel
              dealTypeKey={safeSelectedType}
              selectedDeal={ssotDeal as Record<string, unknown> | null}
              savedComponents={savedComponents as Record<string, unknown> | null}
              editingDraft={draftComponents as Record<string, unknown> | null}
              appliedDraft={appliedDraft?.components as Record<string, unknown> | null}
              lastSaveTrace={lastSaveTrace}
              previewState={previewStateLabel}
            />
          </div>
          
          {/* Preview Area */}
          <ScrollArea className="flex-1">
            {!previewsEnabled ? (
              <div className="p-4 h-full">
                <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 flex flex-col items-center justify-center min-h-[300px] bg-muted/10">
                  <EyeOff className="h-10 w-10 text-muted-foreground/40 mb-3" />
                  <h3 className="text-base font-medium text-muted-foreground mb-1">Previews disabled</h3>
                  <p className="text-sm text-muted-foreground/70 text-center max-w-md mb-3">
                    Enable previews to see live card rendering. Changes are saved even with previews disabled.
                  </p>
                  <Button variant="outline" size="sm" onClick={togglePreviews} className="gap-2">
                    <Eye className="h-4 w-4" />
                    Enable previews
                  </Button>
                </div>
              </div>
            ) : (
              <div className="p-4">
                {!ssotDeal ? (
                  <div className="border rounded-lg p-8 bg-amber-50 text-center">
                    <AlertCircle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
                    <p className="text-sm text-amber-700 font-medium">No deal selected</p>
                    <p className="text-xs text-amber-600 mt-1">Select a sample deal to see the live preview</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-xs">
                        {previewStateLabel === "applied" ? "Applied" : "Saved"}
                      </Badge>
                      <span className="text-sm text-muted-foreground">Full Card Preview</span>
                      {selectedComponentKey && (
                        <span className="text-xs text-primary">• Highlighting: {selectedComponent?.label}</span>
                      )}
                    </div>
                    <LiveCardPreviewWithHighlight
                      selectedComponentKey={selectedComponentKey}
                      deal={ssotDeal as any}
                      dealType={safeSelectedType}
                      draftOverrides={v2DraftOverrides}
                      componentsOverride={effectiveComponents as Record<string, unknown> ?? null}
                      hasUnsavedChanges={hasUnsavedChanges}
                    />
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
}

export default ComponentsStudio;
